import React, { useState, useEffect, useMemo } from 'react';
import LinkLabel from '../../uiComponents/LinkLabel.js';
import { TableContainer, TableBody } from '@mui/material';
import MuiTableHeader from '../../uiComponents/MuiTable/MuiTableHeader.js';
import MuiTablePagination from '../../uiComponents/MuiTable/MuiTablePagination.js';
import { stableSort, getComparator } from '../../uiComponents/MuiTable/MuiHelper.js'
import { StyledPaper, StyledTable, StyledTableRow, StyledTableCell } from '../../uiComponents/MuiTable/StyledTable.js';
import { DateUtils } from '../../utils/DateUtils.js';
import TableFilterConfig from '../../uiComponents/MuiTable/TableFilterConfig.js';
import { loadViewState, saveTableViewState } from '../../utils/ProgramSummaryViewState.js';

//#region create columns header array
const columns = [
  {label: 'Patient ID', id: 'patientId',numeric: false, disablePadding: false},
  {label: 'Date Uploaded',id: 'dateUploaded',numeric: false, disablePadding: false},
  {label: 'Abnormal Biomarkers',id: 'cutoffFormulaString',numeric: false, disablePadding: false},
  {label: 'Potential Condition',id: 'potentialConditionsString',numeric: false, disablePadding: false},
  {label: 'Number of Variant Calls',id: 'numVariants',numeric: true, disablePadding: false,align:'center'},
 ];
//#endregion
export default function PatientTable({ data, programId, breadCrumbs, viewScope }) {
  // Filter/sort/pagination saved when the user last drilled in from this program.
  // Persisted only when a viewScope is provided (Program Summary / Program Dashboard);
  // callers that omit it (if any) opt out. The scope keeps each page's snapshot independent.
  const savedView = useMemo(() => (viewScope ? loadViewState(viewScope, programId).patients || {} : {}), [viewScope, programId]);
  const [rows, setRows] = useState(data);   //hold original dataset
  const [tableData, setTableData] = useState([]); //  
  //props on sort table
  const [order, setOrder] = useState(savedView.order || 'desc');
  const [orderBy, setOrderBy] = useState(savedView.orderBy || 'dateUploaded');
  //prop for paging
  const [page, setPage] = useState(savedView.page ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(savedView.rowsPerPage || 10);

  // Sync with refreshed parent data (the table is kept mounted across polls).
  useEffect(() => {
    setRows(data);
  }, [data]);

  // tableData is driven solely by TableFilterConfig (onFilteredDataChange) so a
  // restored/active filter isn't overwritten with the full dataset on mount/refresh.

  // Persist the current sort/pagination so a return to the page restores it.
  useEffect(() => {
    if (!viewScope) return;
    saveTableViewState(viewScope, programId, 'patients', { order, orderBy, page, rowsPerPage });
  }, [viewScope, programId, order, orderBy, page, rowsPerPage]);

  // Keep a restored/active page valid: clamp to the last page when filtered data shrinks.
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(tableData.length / rowsPerPage) - 1);
    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [tableData, rowsPerPage, page]);
  //#region Sorting 
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };
  //#endregion

  //#region Paging
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };    

  return (
    <>
    <div className="tableFilterGrid">
      <TableFilterConfig 
        rows={rows}
        initialSearchFilter={savedView.searchFilter || ''}
        initialFilterBy={savedView.filterBy || ''}
        onFilterCriteriaChange={(type, value) => {
          if (viewScope) {
            saveTableViewState(viewScope, programId, 'patients', { searchFilter: type, filterBy: value });
          }
          setPage(0);
        }}
        onFilteredDataChange={(filtered) => {
          setTableData(filtered);
        }}
        filterConfig={[
          { type: 'condition', label: 'Condition(s)', field: 'potentialConditionsString' },
          { type: 'abnormalBiomarker', label: 'Abnormal Biomarker(s)', field: 'abnormalBiomarkersString' },
        ]}
      />
    </div>
    <div>
      <StyledPaper>       
        <TableContainer>
          <StyledTable 
            aria-label="patient table" >
            <MuiTableHeader 
              headCells={columns} 
              order={order}  
              orderBy={orderBy} 
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {stableSort(tableData, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => { // use index for key 
                  return(
                  <StyledTableRow tabIndex={-1} key={index}> 
                    <StyledTableCell style={{width: '25%'}}>
                      <LinkLabel  
                        for={row.patientId}
                        path={'/patientdetails/' + row.patientId}
                        text={row.patientId}
                        state={ { programId: programId, variantName: row.variantName, breadCrumbs: breadCrumbs, potentialConditionsString: row.potentialConditionsString } }
                      />
                    </StyledTableCell>
                    <StyledTableCell>{DateUtils.FormatLocalDate(row.dateUploaded)}</StyledTableCell>
                    <StyledTableCell>{row.cutoffFormulaString?.split(';').map((item, i) => (
                      <span key={i}>{item.trim()}{i < row.cutoffFormulaString.split(';').length - 1 && <br />}</span>
                    ))}</StyledTableCell>
                    <StyledTableCell>{row.potentialConditionsString}</StyledTableCell>
                    <StyledTableCell align='center'>{row.numVariants === undefined
                      ? 'N/A'
                      : row.numVariants}
                    </StyledTableCell>
                  </StyledTableRow>
                  );
                })
              }
            </TableBody>
          </StyledTable>
        </TableContainer>
        <MuiTablePagination 
          rowsPerPage={rowsPerPage}
          page={page}
          count={tableData.length}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage} />
      </StyledPaper>
    </div>
    </>
  );
}

